import { Dialog, DialogContent, DialogOverlay } from "@radix-ui/react-dialog";
import { TypeBadge, DurationPill, UserAvatar } from "./atoms";
import styles from "../../../../styles/NavigationLogs.module.css";
import { GetNavigationLogsDto } from "./types";

interface LogDetailsModalProps {
  log: GetNavigationLogsDto | null;
  open: boolean;
  onClose: () => void;
}

export const LogDetailsModal = ({ log, open, onClose }: LogDetailsModalProps) => {
  if (!log) return null;

  const parsedData = tryParse(log?.data)??"{}";
  const parsedChange = tryParse(log?.change)??"{}";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogOverlay className={styles.overlay} />
      <DialogContent className={styles.detailPanel} aria-describedby={undefined}>
        <div className={styles.detailHead}>
          <div>
            <div className={styles.detailTitle}>{log.action}</div>
            <div className={styles.detailSub}>
              <TypeBadge type={log.type} /> <span className={styles.muted}>{log.page}</span>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Fechar">✕</button>
        </div>

        <div className={styles.detailBody}>
          {(log.fullname || log.email) && (
            <div className={styles.userChip}>
              <UserAvatar name={log.fullname} />
              <div>
                <div className={styles.userName}>{log.fullname ?? "—"}</div>
                <div className={styles.userEmail}>{log.email ?? "—"}</div>
                {log.userId && <div className={styles.userId}>{log.userId}</div>}
              </div>
            </div>
          )}

          <div className={styles.detailGrid}>
            <Field label="ID" value={log.id} mono />
            <Field label="Session" value={log.sessionId} mono />
            <Field label="Duração">
              <DurationPill duration={log.duration} />
            </Field>
            <Field label="IP" value={log.ip ?? "—"} mono />
            <Field label="Correlation ID" value={log.correlationId ?? "—"} mono />
          </div>

          {log.userAgent && (
            <Section title="User Agent">
              <pre className={styles.preBlock}>{log.userAgent}</pre>
            </Section>
          )}

          {parsedData && (
            <Section title="Data">
              <pre className={styles.preBlock}>{JSON.stringify(parsedData, null, 2)}</pre>
            </Section>
          )}

          {parsedChange && (
            <Section title="Change">
              <pre className={styles.preBlock}>{JSON.stringify(parsedChange, null, 2)}</pre>
            </Section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

function Field({
  label,
  value,
  mono,
  children,
}: {
  label: string;
  value?: string;
  mono?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className={styles.detailField}>
      <div className={styles.fieldLabel}>{label}</div>
      {children ?? (
        <div className={`${styles.fieldValue} ${mono ? styles.mono : ""}`}>
          {value}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className={styles.detailSection}>
      <div className={styles.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function tryParse(str?: string | null): unknown | null {
  if (!str) return null;
  try { return JSON.parse(str); } catch { return str; }
}
